import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { getPhoneCountries, searchPhoneCountries, getCountryByIso } from '../utils/phoneCountries';
import {
  splitE164,
  composeE164,
  formatAsYouType,
  examplePlaceholder,
  validatePhone,
} from '../utils/phoneValue';

const CountryFlag = ({ iso }) => {
  const [failed, setFailed] = useState(false);
  if (!iso || failed) {
    return <span className="text-[10px] font-semibold text-gray-500">{iso || ''}</span>;
  }
  return (
    <img
      src={`https://flagcdn.com/w20/${iso.toLowerCase()}.png`}
      srcSet={`https://flagcdn.com/w40/${iso.toLowerCase()}.png 2x`}
      alt=""
      width={20}
      height={15}
      loading="lazy"
      className="w-5 h-auto rounded-[2px] inline-block"
      onError={() => setFailed(true)}
    />
  );
};

const CustomPhoneInput = ({
  value = '',
  onChange,
  onValidationChange,
  onBlur,
  placeholder,
  className = '',
  error = null,
  required = false,
  defaultCountry = 'KE',
  countryClassName = 'w-fit min-w-[5.25rem] max-w-[6.5rem]',
  id,
  name,
  disabled = false,
}) => {
  const [iso, setIso] = useState(defaultCountry);
  const [national, setNational] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [localError, setLocalError] = useState(null);
  const [touched, setTouched] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(0);

  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);
  const searchRef = useRef(null);
  const nationalRef = useRef(null);
  const lastEmitted = useRef('');

  const selectedCountry = getCountryByIso(iso) || getCountryByIso(defaultCountry);
  const displayPlaceholder = placeholder || examplePlaceholder(iso);

  const filteredCountries = useMemo(
    () => searchPhoneCountries(searchTerm),
    [searchTerm],
  );

  const emitValue = useCallback(
    (nextIso, nextNational, { showErrors = touched } = {}) => {
      const e164 = composeE164(nextIso, nextNational);
      const result = validatePhone(e164, { required });

      if (e164 !== lastEmitted.current) {
        lastEmitted.current = e164;
        onChange?.(e164);
      }

      onValidationChange?.(result.isValid);

      if (showErrors && !result.isValid && result.message) {
        setLocalError(result.message);
      } else if (result.isValid) {
        setLocalError(null);
      }

      return result;
    },
    [onChange, onValidationChange, required, touched],
  );

  // Sync external value → local state (skip when this component just emitted it).
  useEffect(() => {
    if (value === lastEmitted.current) return;

    const { iso: parsedIso, national: parsedNational } = splitE164(value, defaultCountry);
    setIso(parsedIso || defaultCountry);
    setNational(parsedNational ? formatAsYouType(parsedNational, parsedIso || defaultCountry) : '');
    lastEmitted.current = value || '';

    const e164 = value || composeE164(parsedIso || defaultCountry, parsedNational);
    const result = validatePhone(e164, { required });
    onValidationChange?.(result.isValid);
    if (touched && !result.isValid && result.message) {
      setLocalError(result.message);
    }
  }, [value, defaultCountry, required, onValidationChange, touched]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
        setSearchTerm('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (showDropdown) {
      setHighlightIndex(0);
      setTimeout(() => searchRef.current?.focus(), 0);
    }
  }, [showDropdown]);

  const selectCountry = (country) => {
    setIso(country.iso);
    setShowDropdown(false);
    setSearchTerm('');
    const digits = national.replace(/\D/g, '');
    emitValue(country.iso, digits);
    triggerRef.current?.focus();
  };

  const handleNationalChange = (e) => {
    const raw = e.target.value;
    const caretAtEnd = e.target.selectionStart === raw.length;

    if (raw.startsWith('+')) {
      const { iso: pastedIso, national: pastedNational } = splitE164(raw, iso);
      const formatted = formatAsYouType(pastedNational, pastedIso);
      setIso(pastedIso || iso);
      setNational(formatted);
      emitValue(pastedIso || iso, pastedNational);
      return;
    }

    const digits = raw.replace(/[^\d\s\-()]/g, '');
    // Reformat on append only to avoid caret jumps when editing mid-string.
    const formatted = caretAtEnd ? formatAsYouType(digits, iso) : digits;
    setNational(formatted);
    emitValue(iso, digits.replace(/\D/g, ''));
  };

  const handleDropdownKeyDown = (e) => {
    if (!showDropdown) {
      if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setShowDropdown(true);
      }
      return;
    }

    if (e.key === 'Escape') {
      e.preventDefault();
      setShowDropdown(false);
      setSearchTerm('');
      triggerRef.current?.focus();
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex((i) => Math.min(i + 1, filteredCountries.length - 1));
      return;
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex((i) => Math.max(i - 1, 0));
      return;
    }

    if (e.key === 'Home') {
      e.preventDefault();
      setHighlightIndex(0);
      return;
    }

    if (e.key === 'End') {
      e.preventDefault();
      setHighlightIndex(Math.max(filteredCountries.length - 1, 0));
      return;
    }

    if (e.key === 'Enter' && filteredCountries[highlightIndex]) {
      e.preventDefault();
      selectCountry(filteredCountries[highlightIndex]);
    }
  };

  const visibleError = error || (touched ? localError : null);
  const errorId = id ? `${id}-error` : undefined;

  return (
    <div className={`relative w-full min-w-0 ${className}`}>
      <div className="flex w-full min-w-0 gap-2">
        <div className={`shrink-0 relative ${countryClassName}`} ref={dropdownRef}>
          <button
            ref={triggerRef}
            type="button"
            id={id ? `${id}-country` : undefined}
            disabled={disabled}
            aria-haspopup="listbox"
            aria-expanded={showDropdown}
            aria-label="Country code"
            onKeyDown={handleDropdownKeyDown}
            onClick={() => !disabled && setShowDropdown((open) => !open)}
            className="w-full px-2 py-3 min-h-[44px] border border-stone-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-riverside-red focus:border-transparent bg-stone-50 text-left flex items-center gap-1 transition-all disabled:opacity-60"
          >
            <span className="flex items-center gap-1 min-w-0 truncate text-sm">
              <CountryFlag iso={selectedCountry?.iso} />
              <span className="font-medium tabular-nums">{selectedCountry?.code}</span>
            </span>
            <span className="text-[9px] text-stone-400 shrink-0" aria-hidden="true">▼</span>
          </button>

          {showDropdown && (
            <div
              role="listbox"
              aria-label="Select country"
              className="absolute z-20 w-72 sm:w-80 mt-1 bg-white border border-stone-200 rounded-xl shadow-lg max-h-72 overflow-hidden left-0"
            >
              <div className="p-2 border-b border-stone-200">
                <input
                  ref={searchRef}
                  type="text"
                  placeholder="Search countries…"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setHighlightIndex(0);
                  }}
                  onKeyDown={handleDropdownKeyDown}
                  className="w-full px-2 py-1.5 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-riverside-red"
                />
              </div>
              <div className="max-h-56 overflow-y-auto">
                {filteredCountries.map((country, index) => (
                  <button
                    key={country.iso}
                    type="button"
                    role="option"
                    aria-selected={country.iso === iso}
                    onMouseEnter={() => setHighlightIndex(index)}
                    onClick={() => selectCountry(country)}
                    className={`w-full px-3 py-2 text-left flex items-center gap-2 text-sm ${
                      index === highlightIndex ? 'bg-stone-100' : 'hover:bg-stone-50'
                    }`}
                  >
                    <CountryFlag iso={country.iso} />
                    <span className="font-medium shrink-0">{country.code}</span>
                    <span className="text-stone-600 truncate">{country.name}</span>
                  </button>
                ))}
                {filteredCountries.length === 0 && (
                  <div className="px-3 py-2 text-stone-500 text-sm">No countries found</div>
                )}
                {!searchTerm && getPhoneCountries().length > filteredCountries.length && (
                  <div className="px-3 py-2 text-stone-400 text-xs border-t border-stone-100">
                    Type to search all countries
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <input
            ref={nationalRef}
            type="tel"
            id={id}
            name={name}
            value={national}
            disabled={disabled}
            onChange={handleNationalChange}
            onBlur={() => {
              setTouched(true);
              emitValue(iso, national.replace(/\D/g, ''), { showErrors: true });
              onBlur?.();
            }}
            placeholder={displayPlaceholder}
            required={required}
            inputMode="tel"
            autoComplete="tel"
            aria-invalid={visibleError ? 'true' : 'false'}
            aria-describedby={visibleError && errorId ? errorId : undefined}
            className="w-full min-w-0 px-3 py-3 min-h-[44px] border border-stone-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-riverside-red focus:border-transparent bg-stone-50 focus:bg-white transition-all disabled:opacity-60"
          />
        </div>
      </div>

      {visibleError && (
        <p id={errorId} className="text-red-600 text-sm mt-1" role="alert">
          {visibleError}
        </p>
      )}
    </div>
  );
};

export default CustomPhoneInput;
